/**
 * AdminLegalPage — the CLCO's operational surface.
 *
 * Five views, switched by sub-tab:
 *   • Tickets       — open / in-review legal_tickets sorted by SLA
 *   • Risk register — open + mitigating risks
 *   • Agents        — kill-switch + telemetry per agent
 *   • Audit         — recent audit_log rows + verify-chain button
 *   • Policies      — current + historical policy_versions
 *
 * Authentication: re-uses the admin JWT in localStorage. Server-side
 * routes under /api/legal/admin/* enforce admin auth via the existing
 * get_admin_user dependency.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Scale, Shield, AlertTriangle, Activity, FileText, RefreshCw,
  CheckCircle2, XCircle, Pause, Play, Filter, ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getAdminToken } from "@/lib/authStorage";
import { API } from "@/App";
import { toast } from "sonner";


const SUB_TABS = [
  { id: "tickets",  label: "Tickets",       icon: Scale,         desc: "CLCO review queue" },
  { id: "risks",    label: "Risk register", icon: AlertTriangle, desc: "Open + mitigating risks" },
  { id: "agents",   label: "Agents",        icon: Activity,      desc: "Kill-switch + telemetry" },
  { id: "audit",    label: "Audit chain",   icon: Shield,        desc: "Recent rows + verify" },
  { id: "policies", label: "Policies",      icon: FileText,      desc: "Version history" },
];


function priorityClass(p) {
  return {
    low:      "text-slateline-500",
    medium:   "text-slateline-700",
    high:     "text-amber-600 font-medium",
    critical: "text-red-600 font-bold",
  }[p] || "text-slateline-500";
}


function timeUntil(iso) {
  if (!iso) return "—";
  const due = new Date(iso);
  const now = new Date();
  const diff = (due - now) / 1000;
  if (diff < 0) return `OVERDUE ${Math.floor(-diff / 3600)}h`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}


function authHeaders() {
  const tok = getAdminToken();
  return { Authorization: tok ? `Bearer ${tok}` : "", "Content-Type": "application/json" };
}


/**
 * Robust JSON fetch.
 *
 * Three things this guards against:
 *   1. "body stream already read" — calls .clone().json() so any external
 *      consumer (devtools, HMR, browser extension) reading the original
 *      stream doesn't break us.
 *   2. Non-2xx responses — surfaces them as thrown Errors with the status
 *      so callers can render an error state instead of crashing on
 *      `d.field` access against an error payload.
 *   3. Network failures — `fetch` rejecting throws too; same catch path.
 *
 * Returns the parsed JSON on success; throws Error on any failure.
 */
async function fetchJson(url, opts = {}) {
  const res = await fetch(url, opts);
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      detail = (await res.clone().text()) || detail;
    } catch (_) { /* ignore — fall through with status */ }
    throw new Error(detail);
  }
  return res.clone().json();
}


// ─────────────────────────────────────────────────────────────────────
// Tickets
// ─────────────────────────────────────────────────────────────────────

function TicketsView() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("open");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchJson(
        `${API}/legal/admin/tickets?status=${encodeURIComponent(statusFilter)}`,
        { headers: authHeaders() },
      );
      setTickets(d.tickets || []);
    } catch (e) {
      setError(e.message || "Failed to load tickets");
      setTickets([]);
    } finally { setLoading(false); }
  }

  useEffect(() => {
    load();
    // Re-fetch tickets when the status filter changes. `load` is a
    // component-local async function that closes over the latest filter;
    // adding it to deps would require useCallback for no behaviour change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  async function resolveTicket(id, status, note) {
    try {
      await fetchJson(`${API}/legal/admin/tickets/${id}/resolve`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ status, resolution_note: note }),
      });
      toast.success(`Ticket ${status}`);
      load();
    } catch (e) {
      toast.error(`Failed to resolve ticket: ${e.message}`);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm">
          <Filter className="w-4 h-4 text-slateline-500" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-slateline-200 rounded-md px-2 py-1 bg-canvas-card">
            <option value="open">Open</option>
            <option value="in_review">In review</option>
            <option value="resolved">Resolved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>
        </div>
        <Button onClick={load} variant="outline" className="rounded-md">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {error && (
        <p className="text-sm text-red-600 mb-3">{error}</p>
      )}
      {loading ? <p className="text-sm text-slateline-500">Loading…</p> :
       tickets.length === 0 ? <p className="text-sm text-slateline-500">No tickets.</p> :
       <div className="space-y-3">
         {tickets.map((t) => (
           <div key={t.id} className="border border-slateline-200 rounded-md p-4 bg-canvas-card">
             <div className="flex items-start justify-between gap-4">
               <div className="flex-1 min-w-0">
                 <div className="flex items-center gap-2 text-xs text-slateline-500 mb-1">
                   <span className="uppercase tracking-wide">{t.category}</span>
                   <span>·</span>
                   <span className={priorityClass(t.priority)}>{t.priority?.toUpperCase()}</span>
                   <span>·</span>
                   <span>SLA in {timeUntil(t.sla_due_at)}</span>
                   <span>·</span>
                   <span>opened by {t.opened_by_agent}</span>
                 </div>
                 <p className="font-display text-base text-ink">{t.subject}</p>
                 {t.payload?.agent_decision?.rationale && (
                   <p className="text-sm text-slateline-600 mt-1 italic line-clamp-2">
                     {t.payload.agent_decision.rationale}
                   </p>
                 )}
               </div>
               {t.status === "open" || t.status === "in_review" ? (
                 <div className="flex gap-2">
                   <Button size="sm" onClick={() => resolveTicket(t.id, "resolved", "Approved by CLCO")}
                     className="bg-brand-green hover:bg-brand-green/90 text-white rounded-md">
                     <CheckCircle2 className="w-4 h-4 mr-1" /> Resolve
                   </Button>
                   <Button size="sm" variant="outline" onClick={() => resolveTicket(t.id, "rejected", "Rejected by CLCO")}
                     className="rounded-md">
                     <XCircle className="w-4 h-4 mr-1" /> Reject
                   </Button>
                 </div>
               ) : (
                 <span className="text-xs text-slateline-500">{t.status}</span>
               )}
             </div>
           </div>
         ))}
       </div>
      }
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Agents (kill-switch)
// ─────────────────────────────────────────────────────────────────────

function AgentsView() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchJson(`${API}/legal/admin/agents`, { headers: authHeaders() });
      setAgents(d.agents || []);
    } catch (e) {
      setError(e.message || "Failed to load agents");
      setAgents([]);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function toggle(name, enabled) {
    const endpoint = enabled ? "enable" : "disable";
    const body = enabled ? null : JSON.stringify({ reason: prompt("Reason for kill-switch?") || "manual disable" });
    try {
      await fetchJson(`${API}/legal/admin/agents/${name}/${endpoint}`, {
        method: "POST",
        headers: authHeaders(),
        body,
      });
      toast.success(`${name}: ${enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) {
      toast.error(`Failed to update: ${e.message}`);
    }
  }

  return (
    <div>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {loading ? <p className="text-sm text-slateline-500">Loading…</p> :
       <div className="space-y-2">
         {agents.map((a) => (
           <div key={a.name} className="border border-slateline-200 rounded-md p-3 bg-canvas-card flex items-center justify-between">
             <div>
               <div className="flex items-center gap-3">
                 <span className={`w-2 h-2 rounded-full ${a.enabled ? "bg-brand-green" : "bg-red-500"}`} />
                 <p className="font-medium text-ink">{a.name}</p>
                 <span className="text-xs text-slateline-500">v{a.pinned_version}</span>
                 {!a.enabled && (
                   <span className="text-xs text-red-600">disabled by {a.disabled_by} — {a.disabled_reason}</span>
                 )}
               </div>
               <p className="text-xs text-slateline-500 mt-1">
                 {a.total_invocations || 0} runs · {a.total_escalations || 0} escalations · last:&nbsp;
                 {a.last_invoked_at ? new Date(a.last_invoked_at).toLocaleString() : "never"}
                 {a.last_disposition && <span> ({a.last_disposition})</span>}
               </p>
             </div>
             <Button size="sm" variant="outline" onClick={() => toggle(a.name, !a.enabled)} className="rounded-md">
               {a.enabled ? <><Pause className="w-4 h-4 mr-1" /> Disable</> : <><Play className="w-4 h-4 mr-1" /> Enable</>}
             </Button>
           </div>
         ))}
       </div>
      }
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Risk register
// ─────────────────────────────────────────────────────────────────────

function RisksView() {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = await fetchJson(`${API}/legal/admin/risk-register?status=open`, { headers: authHeaders() });
        setRisks(d.risks || []);
      } catch (e) {
        setError(e.message || "Failed to load risks");
      } finally { setLoading(false); }
    })();
  }, []);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  return loading ? <p className="text-sm text-slateline-500">Loading…</p> :
    risks.length === 0 ? <p className="text-sm text-slateline-500">No open risks. (Watch this space.)</p> :
    <div className="space-y-3">
      {risks.map((r) => (
        <div key={r.id} className="border border-slateline-200 rounded-md p-4 bg-canvas-card">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-xs text-slateline-500 mb-1">
                <span className="uppercase">{r.category}</span> ·
                <span className={priorityClass(r.severity)}>{r.severity?.toUpperCase()}</span>
              </div>
              <p className="font-display text-base text-ink">{r.title}</p>
              <p className="text-sm text-slateline-600 mt-1">{r.description}</p>
              <p className="text-xs text-slateline-500 mt-2">
                Identified by {r.identified_by} on {new Date(r.identified_at).toLocaleDateString()}
                {r.next_review_at && <> · next review {new Date(r.next_review_at).toLocaleDateString()}</>}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>;
}


// ─────────────────────────────────────────────────────────────────────
// Audit
// ─────────────────────────────────────────────────────────────────────

function AuditView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = await fetchJson(`${API}/legal/admin/audit?limit=50`, { headers: authHeaders() });
        setRows(d.rows || []);
      } catch (e) {
        setError(e.message || "Failed to load audit log");
      } finally { setLoading(false); }
    })();
  }, []);

  async function verifyChain() {
    setVerifying(true);
    try {
      const d = await fetchJson(`${API}/legal/admin/audit/verify`, { method: "POST", headers: authHeaders() });
      if (d.intact) {
        toast.success(`Chain intact: ${d.rows_checked} rows verified`);
      } else {
        toast.error(`Chain broken at row ${d.first_divergent_id}: ${d.error}`);
      }
    } catch (e) {
      toast.error(`Verify failed: ${e.message}`);
    } finally { setVerifying(false); }
  }

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button onClick={verifyChain} disabled={verifying} className="bg-brand-green hover:bg-brand-green/90 text-white rounded-md">
          <Shield className="w-4 h-4 mr-2" />
          {verifying ? "Verifying…" : "Verify chain integrity"}
        </Button>
      </div>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {loading ? <p className="text-sm text-slateline-500">Loading…</p> :
        <div className="space-y-1">
          {rows.map((r) => (
            <div key={r.id} className="text-xs font-mono py-1 border-b border-slateline-100">
              <span className="text-slateline-400">#{r.id}</span>{" "}
              <span className="text-slateline-500">{new Date(r.ts).toLocaleString()}</span>{" "}
              <span className="font-medium">{r.agent_name}</span>{" "}
              <span className="text-brand-green">{r.event_type}</span>{" "}
              <span className="text-slateline-500">row {r.row_hash?.slice(0, 8)}…</span>
            </div>
          ))}
        </div>}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Policies
// ─────────────────────────────────────────────────────────────────────

function PoliciesView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = await fetchJson(`${API}/legal/admin/policies`, { headers: authHeaders() });
        setRows(d.policy_versions || []);
      } catch (e) {
        setError(e.message || "Failed to load policies");
      } finally { setLoading(false); }
    })();
  }, []);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  return loading ? <p className="text-sm text-slateline-500">Loading…</p> :
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slateline-200 text-left text-slateline-500">
          <th className="py-2">Policy</th>
          <th>Version</th>
          <th>Effective from</th>
          <th>Effective until</th>
          <th>Approved by</th>
          <th>Hash</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-slateline-100">
            <td className="py-2">
              {r.view_url ? (
                <a
                  href={r.view_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-green underline hover:no-underline inline-flex items-center gap-1"
                  title={`Open ${r.policy_id} v${r.version}`}
                >
                  {r.policy_id}
                  <ArrowLeft className="w-3 h-3 -rotate-[135deg]" />
                </a>
              ) : (
                <span>{r.policy_id}</span>
              )}
            </td>
            <td>{r.version}</td>
            <td>{r.effective_from?.split("T")[0]}</td>
            <td className={r.effective_until ? "" : "text-brand-green font-medium"}>
              {r.effective_until?.split("T")[0] || "in force"}
            </td>
            <td>{r.approved_by}</td>
            <td className="font-mono text-xs text-slateline-500" title={r.text_hash}>
              {r.text_hash?.slice(0, 10)}…
            </td>
            <td className="text-right">
              {r.view_url && (
                <a
                  href={r.view_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-slateline-500 hover:text-brand-green"
                >
                  view →
                </a>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>;
}


// ─────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────

const VALID_TABS = new Set(["tickets", "risks", "agents", "audit", "policies"]);

export default function AdminLegalPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Allow deep-linking from Mission Control via ?tab=tickets|risks|agents|audit|policies
  const initialTab = VALID_TABS.has(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "tickets";
  const [activeSub, setActiveSub] = useState(initialTab);

  useEffect(() => {
    if (!getAdminToken()) navigate("/admin/login");
  }, [navigate]);

  return (
    <div className="min-h-screen bg-canvas">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate("/admin/dashboard")}
          className="inline-flex items-center gap-2 text-slateline-500 hover:text-ink mb-6 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to admin dashboard
        </button>
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Scale className="w-6 h-6 text-brand-green" />
            <h1 className="font-display text-3xl">Office of the CLCO</h1>
          </div>
          <p className="text-slateline-500">
            Operational surface for the agentic legal-ops function.
          </p>
        </div>

        {/* Sub-tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-slateline-200">
          {SUB_TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSub(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 -mb-px border-b-2 transition-colors ${
                  activeSub === tab.id
                    ? "border-brand-green text-brand-green"
                    : "border-transparent text-slateline-500 hover:text-ink"
                }`}
              >
                <Icon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </div>

        {activeSub === "tickets"  && <TicketsView />}
        {activeSub === "risks"    && <RisksView />}
        {activeSub === "agents"   && <AgentsView />}
        {activeSub === "audit"    && <AuditView />}
        {activeSub === "policies" && <PoliciesView />}
      </div>
    </div>
  );
}
